import { useState } from "react";
import { useNavigate } from "react-router-dom";

const applications = [
  {
    category: "Cables & Wires",
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=260&fit=crop&auto=format",
    items: [
      { name: "EV Charging Cables", use: "Used in EV charging stations for Type 1, Type 2 & CCS connectors across 2W, 3W and 4W vehicles." },
      { name: "Solar DC Cables", use: "High-temperature resistant cables connecting solar panels to inverters in rooftop and ground-mounted systems." },
      { name: "House Wires", use: "FR & FRLS wires for residential and commercial electrical wiring installations." },
      { name: "Submersible Cables", use: "Waterproof cables for submersible pumps used in agriculture and water supply projects." },
    ],
  },
  {
    category: "Chargers & Inverters",
    color: "#0891b2",
    bg: "#f0f9ff",
    border: "#bae6fd",
    image: "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=400&h=260&fit=crop&auto=format",
    items: [
      { name: "AC Chargers", use: "Slow & fast AC chargers for home, office and public EV charging points (3.3kW to 22kW)." },
      { name: "DC Fast Chargers", use: "Rapid DC chargers for highways and commercial locations supporting 30kW to 300kW output." },
      { name: "Solar Inverters", use: "On-grid and off-grid inverters converting solar DC power to AC for home and industrial use." },
      { name: "Hybrid Inverters", use: "Smart inverters managing solar, battery and grid power simultaneously for energy optimization." },
    ],
  },
  {
    category: "Solar",
    color: "#d97706",
    bg: "#fffbeb",
    border: "#fde68a",
    image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=400&h=260&fit=crop&auto=format",
    items: [
      { name: "Solar Panels", use: "Mono & poly crystalline panels for rooftop, ground-mount and floating solar projects." },
      { name: "Solar Pumps", use: "DC and AC solar pumps for agriculture irrigation, reducing diesel dependency across rural India." },
      { name: "Mounting Structures", use: "Aluminum and GI structures for fixed-tilt and single-axis tracking solar panel installations." },
      { name: "Bifacial Panels", use: "High-efficiency panels capturing sunlight from both sides — ideal for large-scale solar farms." },
    ],
  },
  {
    category: "Batteries & IoT",
    color: "#7c3aed",
    bg: "#faf5ff",
    border: "#ddd6fe",
    image: "https://images.unsplash.com/photo-1584277261846-c6a1672ed979?w=400&h=260&fit=crop&auto=format",
    items: [
      { name: "Lithium Batteries", use: "LFP & NMC battery packs for EV retrofits, energy storage systems and solar backup." },
      { name: "Smart Gateways", use: "IoT gateways for remote monitoring of chargers, solar systems and energy assets in real time." },
      { name: "Energy Meters", use: "Smart meters for accurate billing, load monitoring and energy auditing in commercial buildings." },
      { name: "Data Loggers", use: "Industrial data loggers recording power parameters for performance analysis and predictive maintenance." },
    ],
  },
  {
    category: "Spare Parts",
    color: "#dc2626",
    bg: "#fff1f2",
    border: "#fecdd3",
    image: "https://images.unsplash.com/photo-1504222490345-c075b6008014?w=400https://images.unsplash.com/photo-1617886903355-9354bb57751f?w=400&h=260&fit=crop&auto=formath=260https://images.unsplash.com/photo-1617886903355-9354bb57751f?w=400&h=260&fit=crop&auto=formatfit=crophttps://images.unsplash.com/photo-1617886903355-9354bb57751f?w=400&h=260&fit=crop&auto=formatauto=format",
    items: [
      { name: "EV Spare Parts", use: "Controllers, BMS units, connectors and motor parts for electric two and three wheelers." },
      { name: "Inverter Parts", use: "IGBT modules, capacitors and control cards for repairing solar and UPS inverters." },
      { name: "Charger Accessories", use: "Cables, holsters, RFID cards and mounting kits for EV charger installations." },
    ],
  },
  {
    category: "Vehicles",
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    image: "https://images.unsplash.com/photo-1558981403-c5f9899a28bc?w=400&h=260&fit=crop&auto=format",
    items: [
      { name: "2-Wheeler EV", use: "Electric scooters and bikes for last-mile delivery, daily commute and urban mobility solutions." },
      { name: "3-Wheeler EV", use: "Electric rickshaws and cargo vehicles for passenger transport and goods delivery in Indian cities." },
    ],
  },
];

function Applications() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const active = applications[activeTab];

  return (
    <main style={{ background: "#f8fafc", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif" }}>

      {/* HEADER */}
      <div style={{ background: "white", borderBottom: "1px solid #e8eaed", padding: "40px 56px 32px" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto" }}>
          <p style={{ fontSize: 11, fontWeight: 800, color: "#aab0b8", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Sora', sans-serif", marginBottom: 10 }}>
            APPLICATIONS
          </p>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: "clamp(24px,3vw,36px)", fontWeight: 900, color: "#1a1a2e", letterSpacing: "-0.8px", margin: "0 0 8px" }}>
            Real-World Applications
          </h1>
          <p style={{ color: "#6b7280", fontSize: 15, margin: 0 }}>
            Explore how our physical products power India's energy infrastructure — from EV charging to solar, IoT and beyond.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 1160, margin: "0 auto", padding: "40px 56px" }}>

        {/* CATEGORY TABS */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 32 }}>
          {applications.map((app, i) => (
            <button key={i}
              onClick={() => setActiveTab(i)}
              style={{
                padding: "9px 18px", borderRadius: 10,
                border: `1.5px solid ${activeTab === i ? app.color : "#e5e7eb"}`,
                background: activeTab === i ? app.color : "white",
                color: activeTab === i ? "white" : "#374151",
                fontSize: 13, fontWeight: 700, cursor: "pointer",
                fontFamily: "'Sora', sans-serif", transition: "all 0.2s",
                whiteSpace: "nowrap",
              }}>
              {app.category}
            </button>
          ))}
        </div>

        {/* CONTENT AREA */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, alignItems: "start" }}>

          {/* LEFT — Image + Info */}
          <div>
            <div style={{ borderRadius: 16, overflow: "hidden", marginBottom: 20, position: "relative", background: active.bg, height: 260 }}>
              <img
                key={activeTab}
                src={active.image}
                alt={active.category}
                style={{ width: "100%", height: 260, objectFit: "cover", display: "block" }}
              />
              <div style={{
                position: "absolute", bottom: 16, left: 16,
                background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)",
                borderRadius: 8, padding: "6px 14px",
              }}>
                <span style={{ color: "white", fontSize: 13, fontWeight: 700, fontFamily: "'Sora', sans-serif" }}>{active.category}</span>
              </div>
            </div>

            {/* Category overview card */}
            <div style={{ background: active.bg, border: `1.5px solid ${active.border}`, borderRadius: 14, padding: "20px 24px" }}>
              <h3 style={{ fontFamily: "'Sora', sans-serif", fontSize: 16, fontWeight: 800, color: active.color, margin: "0 0 8px" }}>
                About {active.category}
              </h3>
              <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, margin: 0 }}>
                Ecoconnect sources verified {active.category.toLowerCase()} products from trusted Indian vendors — ensuring quality, compliance and competitive pricing for all project scales.
              </p>
              <button
                onClick={() => navigate("/products")}
                style={{ marginTop: 16, padding: "10px 20px", background: active.color, color: "white", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: "'Sora', sans-serif", cursor: "pointer", transition: "all 0.2s" }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
              >
                Browse Products →
              </button>
            </div>
          </div>

          {/* RIGHT — Use cases */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {active.items.map((item, i) => (
              <div key={`${activeTab}-${i}`}
                style={{
                  background: "white", borderRadius: 14,
                  border: "1.5px solid #f1f5f9", padding: "20px 22px",
                  transition: "all 0.2s", cursor: "default",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = active.color + "40";
                  e.currentTarget.style.boxShadow = `0 4px 16px ${active.color}10`;
                  e.currentTarget.style.transform = "translateX(4px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#f1f5f9";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateX(0)";
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: active.bg, border: `1px solid ${active.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: active.color, fontFamily: "'Sora', sans-serif" }}>{String(i+1).padStart(2,"0")}</span>
                  </div>
                  <div>
                    <h4 style={{ fontFamily: "'Sora', sans-serif", fontSize: 14, fontWeight: 800, color: "#1a1a2e", margin: "0 0 5px" }}>{item.name}</h4>
                    <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.65, margin: 0 }}>{item.use}</p>
                  </div>
                </div>
              </div>
            ))}

            {/* Enquire CTA */}
            <div style={{ background: "#1a1a2e", borderRadius: 14, padding: "20px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
              <div>
                <div style={{ fontFamily: "'Sora', sans-serif", fontSize: 14, fontWeight: 800, color: "white", marginBottom: 4 }}>Need a custom quote?</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>Talk to our product experts today.</div>
              </div>
              <button onClick={() => navigate("/contact")}
                style={{ padding: "10px 20px", background: active.color, color: "white", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: "'Sora', sans-serif", cursor: "pointer", whiteSpace: "nowrap" }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
              >
                Get Quote
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default Applications;